import joi from 'joi';
import qs from 'qs';
import React, { useEffect, useRef, useState } from 'react';
import { GetEventReportsFilters } from '@loyalty/shared-common/event-processing-api-client';
import { ProfileAttributes } from '@loyalty/shared-common/profile-api-client';
import {
  QlfFilterDetails,
  QlfMasterDetail,
  QlfSimpleDetail,
} from '@qualifio/ui-commons/dist/components/qlf-master-detail';
import { QlfPlaceholder } from '@qualifio/ui-commons';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { paginationResponse } from '@qualifio/api-client';
import { useTranslation } from 'react-i18next';
import { PaginationFooter } from '../../libs/shared/pagination-footer/PaginationFooter';
import { ProfileDetails } from '../../libs/profiles/components/detail';
import { ProfileDisplayName } from '../../libs/profiles/utils/ProfileDisplayName';
import {
  ProfilesProvider,
  profilesPageSize,
  useProfiles,
} from '../../libs/profiles/ProfileContext';
import { QlfMasterDetailContainer } from '../../libs/shared/layout/QlfMasterDetailContainer';
import { useCustomKeys } from '../../libs/custom-keys/CustomKeysContext';
import { useQueryParams } from '../../libs/shared/use-query-params';

const uuidFiltersSchema = joi.string().uuid();

function Profiles(): React.ReactElement {
  const {
    profiles,
    setProfiles,
    error,
    currentPage,
    selectedProfile,
    setCurrentPage,
    setSelectedProfile,
    getProfileById,
    searchProfiles,
    goToPreviousPage,
    goToNextPage,
    isLoading,
  } = useProfiles();
  const [searchBarContent, setSearchBarContent] = useState<string>('');
  const { getCustomKeys, customKeys } = useCustomKeys();
  const { t } = useTranslation();

  const { current: search$ } = useRef(new Subject<string>());

  const { setParams } = useQueryParams();

  useEffect(() => {
    if (!customKeys.length) {
      getCustomKeys();
    }

    const searchValue = fromQueryParamsToSearchValue(
      qs.parse(window.location.search, { ignoreQueryPrefix: true }) as Record<string, string>,
    );
    if (!searchValue) {
      searchProfiles('', currentPage).catch(console.error);
    } else {
      const uuidValidation = uuidFiltersSchema.validate(searchValue);
      if (uuidValidation.value && !uuidValidation.error) {
        getProfileById(searchValue).then(profile => {
          if (profile) {
            setProfiles(paginationResponse([profile], 1, 1, 10));
          } else {
            setProfiles(paginationResponse([], 0, 1, 10));
          }
        });
      }
    }

    setSearchBarContent(searchValue);
    searchProfiles(searchValue, currentPage);

    const subscription = search$
      .pipe(
        debounceTime(300),
        distinctUntilChanged((a, b) => a === b),
      )
      .subscribe(value => {
        searchProfiles(value, currentPage);
        setCurrentPage(0);
        setSearchBarContent(value);
        setParams({ search: value || '' });
      });
    return () => {
      subscription.unsubscribe();
    };
  }, [setParams]);

  const templateHeader = () => (
    <QlfFilterDetails
      placeholder={t('profiles.sidebar.search')}
      value={searchBarContent}
      onChange={e => {
        search$.next(e.target.value);
        setSearchBarContent(e.target.value);
      }}
    />
  );

  const templateRow = (props: { data: ProfileAttributes }) => {
    const { data: profile } = props;

    return (
      <QlfSimpleDetail
        state={profile.id === selectedProfile?.id ? 'active' : 'default'}
        callback={() => {
          setSelectedProfile(props.data);
        }}
        content={<ProfileDisplayName profile={profile} />}
      />
    );
  };

  const changePage = (step: number) => {
    if (!isLoading('searchProfiles') && (profiles?.data.length === profilesPageSize || step < 0)) {
      if (step > 0) {
        goToNextPage();
      } else {
        goToPreviousPage();
      }
    }
  };

  const templateFooter = (
    <PaginationFooter
      changePage={changePage}
      currentPage={currentPage}
      dataPage={profiles}
      isLoading={isLoading('searchProfiles')}
    />
  );
  return (
    <QlfMasterDetailContainer>
      {error ? (
        <QlfPlaceholder primaryText={t('profiles.errors.loading')} />
      ) : (
        <QlfMasterDetail
          templateHeader={templateHeader}
          templateRow={templateRow}
          templateDetail={ProfileDetails}
          templateFooter={templateFooter}
          items={profiles?.data || []}
        />
      )}
    </QlfMasterDetailContainer>
  );
}

export function ProfilesIndexPage() {
  return (
    <ProfilesProvider>
      <Profiles />
    </ProfilesProvider>
  );
}

const filtersSchema = joi.object({
  search: joi.string().optional(),
});

// TODO change this to a more generic version

function fromQueryParamsToSearchValue(queryParams: Record<string, string>): string | '' {
  const { error, value } = filtersSchema.validate(queryParams, { stripUnknown: true });
  if (error || !value.search) {
    window.history.pushState({}, '', window.location.origin + window.location.pathname);
    return '';
  }
  return value.search;
}
